import { ChangeDetectionStrategy, Component, DestroyRef, Signal, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { TranslateModule } from '@ngx-translate/core';

import { ChromeReadyService } from '../chrome-ready.service';
import { RailTreeService } from '../rail-tree.service';
import { NotificationsService } from './notifications.service';
import { sectionLabelKeyForUrl } from './section-label.util';
import { TopbarBackTarget, TopbarClaimService } from './topbar-claim.service';
import { TopbarQuickCreateComponent } from './topbar-quick-create/topbar-quick-create.component';
import { TopbarNotificationsComponent } from './topbar-notifications/topbar-notifications.component';
import { TopbarTenantSwitcherComponent } from './topbar-tenant-switcher/topbar-tenant-switcher.component';
import { TopbarLanguageSwitcherComponent } from './topbar-language-switcher/topbar-language-switcher.component';
import { TopbarUserMenuComponent } from './topbar-user-menu/topbar-user-menu.component';

/**
 * The shell top bar (NBO-2331): white, 44px. The left slot carries the
 * SECTION label for screens that have adopted the new shell (NBO-2388) and
 * stays empty for every other route — the rail-collapse toggle lives at the
 * rail FOOT (a toggle at top-bar-left was tried and rejected: it reads as
 * browser Back). That reading is what the entity page wants (canon §3b,
 * NBO-2532): while a page claims a back action (`TopbarClaimService`), the
 * label itself is the back action, "‹ Section". A singleton page the rail does
 * not hold (Settings, NBO-2545) claims its own label instead — plain, no
 * chevron.
 *
 * Right cluster in mockup order: + New · notifications · tenant · language ·
 * divider · user. The bell is BUILT but gated on
 * `NotificationsService.enabled`, which stays false until NBO-2207 ships a
 * backend — see that service for the handover note.
 */
@Component({
  selector: 'app-shell-topbar',
  standalone: true,
  imports: [
    TranslateModule,
    TopbarQuickCreateComponent,
    TopbarNotificationsComponent,
    TopbarTenantSwitcherComponent,
    TopbarLanguageSwitcherComponent,
    TopbarUserMenuComponent
  ],
  templateUrl: './shell-topbar.component.html',
  styleUrls: ['./shell-topbar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ShellTopbarComponent {
  readonly notificationsEnabled = this.notificationsService.enabled;

  private readonly chromeReady = inject(ChromeReadyService);
  private readonly railTree = inject(RailTreeService);
  private readonly topbarClaim = inject(TopbarClaimService);

  /** Drives the single fade on the identity cluster (NBO-2331 r4). */
  readonly identityReady = this.chromeReady.identityReady;

  /**
   * The URL the label is judged on. Seeded from the boot URL for the same
   * reason chrome visibility is (NBO-2343): the corrective NavigationEnd sits
   * behind the auth guards and the lazy chunk, and an empty seed would leave a
   * redesigned screen nameless for that whole window.
   */
  private readonly url = signal<string>(window.location.pathname);

  /**
   * i18n key of the current section, or null on a route that still renders
   * its own page header.
   *
   * Resolved from the VIEWER-filtered tree the rail renders
   * (`RailTreeService.visibleWorlds()`), not the structural one, so the label
   * is the rail's string for this viewer — a role-collapsed section reads
   * "Sales" in both places, never "Sales" in the rail and "Action Hub" here.
   * Gated on `navReady` like the rail itself: until the role claim resolves
   * the tree is empty and the rail shows nothing, so the label shows nothing
   * too rather than a structural guess that could flip when roles land.
   */
  readonly sectionLabelKey: Signal<string | null> = computed(() =>
    this.chromeReady.navReady() ? sectionLabelKeyForUrl(this.url(), this.railTree.visibleWorlds()) : null
  );

  /**
   * An entity page's back target, shown once navigation is ready — like the label — so the
   * chevron never paints beside a placeholder word that flips when the roles land.
   */
  readonly backTarget = computed<TopbarBackTarget | null>(() => (this.chromeReady.navReady() ? this.topbarClaim.backTarget() : null));

  /**
   * A singleton page's own label (Settings, NBO-2545), shown once navigation is ready like the
   * section label. It wins over the rail's label: such a page is no rail destination, so that one
   * would be empty — or, under an adopted prefix, the wrong name.
   */
  readonly pageLabelKey = computed<string | null>(() => (this.chromeReady.navReady() ? this.topbarClaim.labelKey() : null));

  /** The section the back action names; plain "Back" on a route the rail has no section for. */
  readonly backLabelKey = computed(() => this.sectionLabelKey() ?? 'common.actions.back');

  constructor(
    private notificationsService: NotificationsService,
    private router: Router,
    destroyRef: DestroyRef
  ) {
    router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(destroyRef)
      )
      // urlAfterRedirects: a guard redirect is judged by the URL the user
      // actually lands on, matching the chrome-visibility tracker.
      .subscribe((event) => this.url.set(event.urlAfterRedirects || event.url));
  }

  /** Through the router, so the page's unsaved-changes guard asks first. */
  goBack(target: TopbarBackTarget): void {
    if (typeof target === 'string') {
      this.router.navigateByUrl(target);
    } else {
      this.router.navigate([...target]);
    }
  }
}
